import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import ImageIcon from "@redotech/redo-icons/image-01.svg";
import { SignupForm } from "@redotech/redo-marketing-common/signup-form/signup-form-schema";
import {
  ButtonSize,
  RedoButtonCloseX,
} from "@redotech/redo-web/arbiter-components/redo-button-close-x";
import { Flex } from "@redotech/redo-web/flex";
import { memo, ReactNode, useRef } from "react";
import { useCloseButtonOnScreen } from "./close-button-position";
import * as signupFormCss from "./signup-form.module.css";

export const SignupFormContainer = memo(function SignupFormContainer({
  form,
  children,
  onClose,
  previewMobile,
  isPreview = false,
}: {
  form: SignupForm;
  children: ReactNode;
  onClose?: () => void;
  previewMobile: boolean;
  isPreview?: boolean;
}) {
  const showMobileView = useScreenType() === ScreenType.MOBILE || previewMobile;
  const signupFormWidth = form.layout.type === "popup" ? "600px" : "100%";
  const signupFormMobileWidth =
    form.layout.type === "popup" ? "375px" : "430px";
  const signupformHeight =
    form.layout.type === "fullscreen" && !isPreview ? "100vh" : "auto";
  const size = {
    width: showMobileView ? signupFormMobileWidth : signupFormWidth,
    height: signupformHeight,
    borderRadius: form.theme.border.cornerRadiusPx,
  };

  const popupBackgroundOpacity =
    (form.theme.overlay.popupOpacityPercent ?? 100) / 100;

  // Convert hex color to RGBA with opacity
  const hexToRgba = (hex: string, opacity: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const closeButtonStyle = {
    color: form.theme.closeButton.color,
    background: form.theme.closeButton.background.enabled
      ? form.theme.closeButton.background.color
      : "transparent",
    cursor: "pointer",
  };

  const formContainerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLDivElement>(null);
  useCloseButtonOnScreen(formContainerRef, closeButtonRef);

  const showImage =
    form.layout.image.style != "no" &&
    !(form.layout.image.hideOnMobile && showMobileView);
  const backgroundImage = form.layout.image.style == "background";
  const vertical =
    showMobileView ||
    form.layout.image.style === "top" ||
    form.layout.image.style === "bottom";
  const imageFirst =
    (showMobileView && form.layout.image.style !== "bottom") ||
    form.layout.image.style === "top" ||
    form.layout.image.style === "left";

  return (
    <Flex
      maxW="full"
      ref={formContainerRef}
      style={{
        ...size,
        backgroundImage: backgroundImage
          ? `url(${form.layout.image.url})`
          : "none",
        backgroundColor: hexToRgba(
          form.layout.backgroundColor,
          popupBackgroundOpacity,
        ),
        backgroundSize: backgroundImage
          ? (form.layout.image.backgroundFit ?? "contain")
          : undefined,
        backgroundPosition: backgroundImage ? "center" : undefined,
        padding: backgroundImage ? "50px" : "0px",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
      w="lg"
    >
      <Flex
        dir="column"
        gap="none"
        style={{
          ...size,
          width:
            backgroundImage && form.layout.type === "fullscreen"
              ? "60%"
              : "100%",
          height:
            backgroundImage && form.layout.type === "fullscreen"
              ? "auto"
              : size.height,
        }}
        w="full"
      >
        <Flex
          dir={vertical ? "column" : "row"}
          gap="none"
          height="full"
          w="full"
        >
          {showImage && !backgroundImage && imageFirst && (
            <SignupImage
              altText={form.layout.image.altText}
              fullscreen={form.layout.type === "fullscreen"}
              imageFirst={imageFirst}
              rounding={form.theme.border.cornerRadiusPx + "px"}
              url={form.layout.image.url}
              vertical={vertical}
            />
          )}
          <Flex
            align="stretch"
            className={signupFormCss.formContent}
            dir="column"
            gap="none"
            justify="center"
            style={{
              width: vertical || !showImage || backgroundImage ? "100%" : "50%",
            }}
          >
            {children}
          </Flex>
          {showImage && !backgroundImage && !imageFirst && (
            <SignupImage
              altText={form.layout.image.altText}
              fullscreen={form.layout.type === "fullscreen"}
              imageFirst={imageFirst}
              rounding={form.theme.border.cornerRadiusPx + "px"}
              url={form.layout.image.url}
              vertical={vertical}
            />
          )}
        </Flex>
        <Flex className={signupFormCss.closeButton} ref={closeButtonRef}>
          {onClose && (
            <RedoButtonCloseX
              onclick={onClose}
              onDarkBackground={false}
              size={ButtonSize.SMALL}
              style={closeButtonStyle}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const SignupImage = memo(function SignupImage({
  url,
  vertical,
  imageFirst,
  fullscreen,
  rounding,
  altText,
}: {
  url: string;
  vertical: boolean;
  imageFirst: boolean;
  fullscreen: boolean;
  rounding?: string;
  altText?: string;
}) {
  const imageRounding = {
    borderRadius: `${imageFirst ? rounding : "0"} ${
      (imageFirst && vertical) || (!imageFirst && !vertical) ? rounding : "0"
    } ${!imageFirst ? rounding : "0"} ${(!imageFirst && vertical) || (imageFirst && !vertical) ? rounding : "0"}`,
  };

  return (
    <Flex
      style={{
        maxHeight: vertical && !fullscreen ? "150px" : "100%",
        width: vertical ? "100%" : "50%",
        overflow: "hidden",
      }}
    >
      {url && (
        <img
          // Empty string signals a decorative image to screen readers (WCAG)
          alt={altText ?? ""}
          className={signupFormCss.imageSection}
          src={url}
          style={{ objectFit: "cover", ...imageRounding }}
        />
      )}

      {!url && (
        <Flex
          align="center"
          className={signupFormCss.imageSectionPlaceholder}
          dir="column"
          justify="center"
          style={imageRounding}
        >
          <ImageIcon />
          Add image
        </Flex>
      )}
    </Flex>
  );
});
